import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import { CloseIcon } from '../../assets/Icons';
import type { ModalProps } from '../../types';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  showCloseButton = true,
}) => {
  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className='fixed inset-0 flex items-center justify-center p-4 bg-black bg-opacity-90 z-[9999]'
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
    >
      <div
        className={cn(
          'bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-visible relative mx-auto',
          className
        )}
        onClick={e => e.stopPropagation()}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className='absolute z-50 -top-5 md:-top-8 -right-3 md:-right-8 w-10 md:w-16 h-10 md:h-16 bg-seeds-yellow hover:bg-hover-yellow rounded-full flex items-center justify-center transition-colors duration-200 focus:outline-none '
            aria-label='Close modal'
          >
            <CloseIcon className='w-6 h-6 md:w-8 md:h-8 text-black' />
          </button>
        )}

        {/* Header */}
        {title && (
          <div className='relative p-4'>
            <h2 className='text-3xl font-satoshi-bold text-[#006543] pr-12 text-center'>
              {title}
            </h2>
          </div>
        )}

        {/* Content */}
        <div className=' max-h-[calc(90vh-80px)] overflow-y-auto'>
          {children}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  if (typeof window !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return null;
};
